<template>
  <div class="login-page-wrapper relative min-h-screen overflow-hidden">
    <!-- Dynamic Form Styling -->
    <component :is="'style'" v-if="dynamicTextStyles">{{ dynamicTextStyles }}</component>

    <!-- Loading overlay (requisição) -->
    <div v-if="!requisicaoFinalizada" class="loading-overlay">
      <div class="spinner"></div>
    </div>

    <!-- Loading overlay (background config) -->
    <div v-if="!isBackgroundReady" class="fixed inset-0 bg-black z-50 flex items-center justify-center">
      <div class="flex flex-col items-center gap-4">
        <div class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
        <p class="text-white/80 text-sm">Carregando...</p>
      </div>
    </div>

    <!-- Solid gradient background -->
    <div class="auth-background" :style="customBackgroundStyle"></div>

    <!-- Subtle overlay for depth -->
    <div class="auth-overlay"></div>

    <!-- Content layer -->
    <div class="login-glass relative z-10 min-h-screen flex flex-col px-6 py-10 pb-6 sm:py-12 sm:pb-8 lg:px-0 lg:py-0">

      <!-- TOP: Theme toggle only -->
      <div class="flex items-center justify-end w-full max-w-sm mx-auto lg:max-w-none lg:px-12 lg:pt-8 lg:pb-4">
        <div class="flex items-center gap-2">
          <BackgroundCustomizer
            ref="backgroundCustomizer"
            :nomeEmpresa="nomeEmpresa"
            :showControls="showBackgroundCustomizer"
            @background-change="handleBackgroundChange"
            @button-color-change="handleButtonColorChange"
            @form-styling-change="handleFormStyling"
          />
          
          <button
            @click="handleThemeClick"
            class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Toggle theme"
          :aria-pressed="isDark"
        >
          <svg
            v-if="!isDark"
            class="w-5 h-5 text-yellow-300"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            />
          </svg>
          <svg
            v-else
            class="w-5 h-5 text-slate-300"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
            />
          </svg>
        </button>
        <!-- Secret input for editor mode activation -->
        <input
          v-if="showSecretInput"
          v-model="secretInput"
          @input="checkSecretCode"
          ref="secretInput"
          type="text"
          class="fixed -left-full opacity-0 w-0 h-0"
          autocomplete="off"
        />
        </div>
      </div>

      <!-- CENTER -->
      <div 
        class="flex-1 flex flex-col justify-center w-full pb-8"
        :class="parentContainerClass"
        :style="parentContainerStyle"
      >

        <!-- ESTADO TOKEN -->
        <div 
          v-if="openToken" 
          class="space-y-6 form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Botão voltar -->
          <button
            @click="voltar()"
            class="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar
          </button>

          <!-- Ícone cadeado -->
          <div class="flex justify-center">
            <div
              class="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          <!-- Título -->
          <h2
            class="text-2xl font-semibold text-center text-white"
          >
            Chave de acesso
          </h2>

          <!-- Descrição -->
          <p class="text-sm text-center text-white/60">
            {{ textoDescricaoToken }}
          </p>

          <!-- Formulário token -->
          <form @submit.prevent="sendRegisterWithToken" class="space-y-4">
            <div>
              <label
                class="block text-sm font-medium mb-2"
              >
                Chave de acesso
              </label>
              <Password
                v-model="token"
                :feedback="false"
                toggleMask
                autocomplete="off"
                @click="clear"
                :class="{ 'border-red-500': erro }"
              />

              
              <p v-if="messageError" class="erro mt-2">{{ messageError }}</p>
            </div>

            <Button
              type="submit"
              :loading="disableBtnConcluir"
              :disabled="disableBtnConcluir"
              label="Concluir"
              class="w-full btn-dynamic-color"
            />
          </form>
        </div>
        <!-- ESTADO FORMULÁRIO -->
        <div 
          v-if="!openToken" 
          class="form-container mt-8 lg:mt-0"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Logo (mobile e desktop) -->
          <div class="flex justify-center mb-6 lg:mb-10">
            <div 
              :class="[
                'inline-flex items-center justify-center px-3 py-2 rounded-xl',
                { 'bg-black/10': !isFormStylingActive }
              ]"
            >
              <img
                :src="logoUrl"
                :class="[
                  'h-12 lg:h-20 w-auto',
                  { 'logo-invert-dark': shouldInvertLogo }
                ]"
                :alt="nomeEmpresa"
                @touchstart="startLogoPress"
                @touchend="endLogoPress"
                @touchcancel="endLogoPress"
              />
            </div>
          </div>

          <!-- Mensagens digitando -->
          <div class="space-y-6 mb-8">
            <!-- "Cadastre-se" -->
            <div class="flex justify-center">
              <div 
                :class="[
                  'inline-flex px-3 py-1.5 rounded-xl',
                  { 'bg-black/10': !isFormStylingActive }
                ]"
              >
                <h2 class="text-2xl sm:text-3xl font-bold text-white text-center">
                  {{ typedText1 }}<span v-if="typedText1.length < welcomeMessage1.length" class="animate-pulse">|</span>
                </h2>
              </div>
            </div>
            
            <!-- "Preencha seus dados..." -->
            <div v-if="showSecondMessage" class="flex justify-center">
              <div class="inline-flex px-3 py-1 rounded-lg">
                <p class="text-sm text-white/60 text-center">
                  {{ typedText2 }}<span v-if="typedText2.length < welcomeMessage2.length" class="animate-pulse">|</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Formulário (aparece após mensagens) -->
          <div v-if="showFormContent">
          <!-- Voltar -->
          <button
            @click="$router.push('/Login')"
            class="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </button>

          <form
            @submit.prevent="modoAutenticacao == 2 ? receberToken() : avancar()"
            class="space-y-4"
          >
            <!-- Tipo cadastro -->
            <div>
              <label
                class="block text-sm font-medium mb-2"
              >
                Tipo de cadastro*
              </label>
              <Dropdown
                v-model="cadastro.selectedTipoCliente"
                :options="tiposClientes"
                optionLabel="label"
                optionValue="value"
                placeholder="Selecione um tipo"
                class="w-full"
                @change="limparCadastro"
              />
            </div>

            <!-- CNPJ (PJ/FU) -->
            <div
              v-if="
                cadastro.selectedTipoCliente == '2' ||
                cadastro.selectedTipoCliente == '3'
              "
            >
              <label
                class="block text-sm font-medium mb-2"
              >
                CNPJ*
              </label>
              <InputText
                v-model="cadastro.cnpj"
                v-mask="'##.###.###/####-##'"
                placeholder="00.000.000/0000-00"
                @blur="verifyCNPJ"
              />
            </div>

            <!-- Nome/Razão Social -->
            <div>
              <label
                class="block text-sm font-medium mb-2"
              >
                {{
                  cadastro.selectedTipoCliente == "2" ||
                  cadastro.selectedTipoCliente == "3"
                    ? "Razão Social*"
                    : "Nome completo*"
                }}
              </label>
              <InputText
                v-model="cadastro.nomeRazaoSocial"
                :disabled="cadastro.selectedTipoCliente != '1'"
              />
            </div>

            <!-- CPF (PF) -->
            <div
              v-if="
                cadastro.selectedTipoCliente == '1' ||
                cadastro.selectedTipoCliente == 'selecione'
              "
            >
              <label
                class="block text-sm font-medium mb-2"
              >
                CPF*
              </label>
              <InputText
                v-model="cadastro.cpf"
                v-mask="'###.###.###-##'"
                placeholder="000.000.000-00"
                :class="{ 'p-invalid': cadastro.cpf && cadastro.cpf.length === 14 && !validarCPF(cadastro.cpf) }"
              />
              <small v-if="cadastro.cpf && cadastro.cpf.length === 14 && !validarCPF(cadastro.cpf)" class="text-red-300 text-sm mt-1.5 block font-medium">
                ⚠️ CPF inválido
              </small>
            </div>

            <!-- Data Constituição (PJ/FU) -->
            <div
              v-if="
                cadastro.selectedTipoCliente == '2' ||
                cadastro.selectedTipoCliente == '3'
              "
            >
              <label
                class="block text-sm font-medium mb-2"
              >
                Data de Constituição*
              </label>
              <InputText
                v-model="cadastro.dataConstituicao"
                v-mask="'##/##/####'"
                disabled
              />
            </div>

            <!-- Email -->
            <div>
              <label
                class="block text-sm font-medium mb-2"
              >
                {{ labelEmail }}
              </label>
              <InputText
                v-model="cadastro.email"
                type="email"
                :placeholder="placeholderEmail"
              />
              <p
                v-if="isApenasPJ"
                class="mt-2 text-xs text-white/50 block"
              >
                Este e-mail será utilizado para acessar a conta e receber
                comunicações.
              </p>
            </div>

            <!-- Apelido (PJ/FU com config) -->
            <div
              v-if="
                configuracaoApelido == 'S' &&
                cadastro.selectedTipoCliente !== '1' &&
                cadastro.selectedTipoCliente !== 'selecione'
              "
            >
              <div class="flex items-center gap-2 mb-2">
                <label
                  class="block text-sm font-medium"
                >
                  Apelido*
                </label>
                <i
                  class="fa fa-info-circle info-icon text-white/50"
                  title="O Apelido de Conta permite que você personalize a identificação das suas contas de forma única."
                ></i>
              </div>
              <InputText
                v-model="cadastro.apelido"
                placeholder="Insira seu apelido"
                @input="validarApelido"
                @blur="checkApelidoLength"
              />
              <p v-if="apelidoError" class="erro mt-1">{{ apelidoError }}</p>
              <p class="text-xs text-white/50 mt-1">
                O apelido deve ter entre 3 e 20 caracteres, podendo incluir
                letras e números.
              </p>
            </div>

            <!-- Botões -->
            <Button
              v-if="modoAutenticacao != 2"
              type="submit"
              label="Avançar"
              class="w-full btn-dynamic-color"
            />
            <Button
              v-else
              type="submit"
              :loading="disableBtnReceberToken"
              :disabled="disableBtnReceberToken"
              label="Receber Código de acesso"
              class="w-full btn-dynamic-color"
            />
          </form>

          <!-- Footer -->
          <div class="mt-6 text-center mb-4">
            <p class="text-sm text-white/60">
              Já possui uma conta?
              <a
                :href="loginLink"
                class="font-medium text-white/80 hover:text-white hover:underline transition-colors"
              >
                Entrar
              </a>
            </p>
          </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  <ModalAvisoGenerico
    :mensagem="messageErrorGenerico"
    :mensagensComplementares="mensagensComplementaresGenerico"
  />
</template>

<script>
import { required, email, minLength } from "@vuelidate/validators";
import validateCpf from "@/utils/validateCpf";
import validateCnpj from "@/utils/validateCnpj";
import api from "@/api/axios";
import { mapGetters } from "vuex";
import { mapActions } from "vuex";
import ModalAvisoGenerico from "../Login/ModalAvisoGenerico.vue";
import BackgroundCustomizer from "@/components/BackgroundCustomizer.vue";
import { useTheme } from "@/composables/useTheme";
import { useEditorMode } from "@/composables/useEditorMode";

export default {
  name: "CadastroInicio",
  components: {
    ModalAvisoGenerico,
    BackgroundCustomizer,
  },
  setup() {
    const { isDark, toggleTheme } = useTheme();
    const { editorModeActive, toggleEditorMode, initEditorMode } = useEditorMode();
    
    initEditorMode();

    return {
      isDark,
      toggleTheme,
      editorModeActive,
      toggleEditorMode,
    };
  },
  data() {
    return {
      nomeEmpresa: "",
      hashToken: "",
      messageErrorGenerico: null,
      mensagensComplementaresGenerico: "",
      requisicaoFinalizada: true,
      modoAutenticacao: 0,
      openToken: false,
      messageError: "",
      token: "",
      erro: false,
      disableBtnReceberToken: false,
      disableBtnConcluir: false,
      cadastro: {
        selectedTipoCliente: "selecione",
        cnpj: null,
        cpf: null,
        nomeRazaoSocial: null,
        email: null,
        apelido: null,
        dataConstituicao: null,
      },
      permitirAvancar: false,
      apelidoError: "",
      tiposClientes: [],
      configuracaoTiposClientes: {},
      configuracaoURLEntrar: "",
      configuracaoApelido: "",
      loginLink: "/Login",
      // Typing animation
      showTypingWelcome: false,
      typedText1: "",
      typedText2: "",
      welcomeMessage1: "",
      welcomeMessage2: "",
      showSecondMessage: false,
      showFormContent: false,
      customBackgroundStyle: {
        background: 'linear-gradient(135deg, #00235C, #003D82, #0056A8)'
      },
      customButtonColor: null,
      isBackgroundReady: false,
      formStylingConfig: null,
      // Mobile logo press to reveal customizer
      showBackgroundCustomizer: false,
      logoPressTimer: null,
      // Secret editor mode
      themeClickCount: 0,
      themeClickTimer: null,
      secretInput: '',
      showSecretInput: false,
    };
  },
  computed: {
    ...mapGetters({
      account: "GET_CREATE_ACCOUNT",
      getConfiguracoes: "GET_CONFIGURACOES",
    }),
    logoUrl() {
      return require("@/" + process.env.VUE_APP_URL_LOGO);
    },
    isApenasPJ() {
      // Verifica se apenas PJ está disponível OU se PJ foi selecionado
      const apenasPJDisponivel =
        this.tiposClientes.length === 1 && this.tiposClientes[0].value === "2";
      const pjSelecionado = this.cadastro.selectedTipoCliente === "2";
      return apenasPJDisponivel || pjSelecionado;
    },
    labelEmail() {
      return this.isApenasPJ ? "E-mail da empresa*" : "E-mail*";
    },
    placeholderEmail() {
      return this.isApenasPJ
        ? "Digite o e-mail que será utilizado na conta"
        : "Insira seu e-mail";
    },
    textoDescricaoToken() {
      return this.isApenasPJ
        ? "Para continuar, digite o código que enviamos para o e-mail da empresa, informado no cadastro."
        : "Para continuar, digite o código que acabamos de enviar para o seu e-mail.";
    },
    detectedDevice() {
      const width = window.innerWidth;
      if (width < 768) return 'mobile';
      if (width < 1024) return 'tablet';
      return 'desktop';
    },
    isFormStylingActive() {
      return this.formStylingConfig?.enabled?.[this.detectedDevice] || false;
    },
    currentFormConfig() {
      if (!this.isFormStylingActive) return null;
      return this.formStylingConfig?.[this.detectedDevice] || null;
    },
    formContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return {};
      const { backgroundColor, opacity, width, minWidth, maxWidth, margin } = this.currentFormConfig;
      return {
        backgroundColor: this.hexToRgba(backgroundColor, opacity / 100),
        backdropFilter: 'blur(16px)',
        borderRadius: '1rem',
        padding: '2rem',
        width: width || '100%',
        minWidth: minWidth || '0px',
        maxWidth: maxWidth || '600px',
        marginLeft: margin || '0rem',
        marginRight: margin || '0rem'
      };
    },
    formContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return '';
      const { alignment } = this.currentFormConfig;
      return alignment === 'left' ? 'mr-auto' : 
             alignment === 'right' ? 'ml-auto' : 'mx-auto';
    },
    parentContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return 'items-center mx-auto';
      }
      const { alignment } = this.currentFormConfig;
      if (alignment === 'left') {
        return 'items-start';
      } else if (alignment === 'right') {
        return 'items-end';
      } else {
        return 'items-center mx-auto';
      }
    },
    parentContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return {};
      }
      const { alignment, alignmentOffset } = this.currentFormConfig;
      const style = {};
      
      if (alignment === 'left') {
        style.paddingLeft = `${alignmentOffset * 0.25}rem`;
      } else if (alignment === 'right') {
        style.paddingRight = `${alignmentOffset * 0.25}rem`;
      }
      
      return style;
    },
    childContainerWidthClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return 'w-full';
      }
      return '';
    },
    shouldInvertLogo() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return false;
      }
      return this.currentFormConfig.logoInvert;
    },
    customCadastroTitle() {
      console.log('🔍 customCadastroTitle chamado');
      console.log('  isFormStylingActive:', this.isFormStylingActive);
      console.log('  cadastroTitle value:', this.currentFormConfig?.customTexts?.cadastroTitle);
      if (this.isFormStylingActive && this.currentFormConfig?.customTexts?.cadastroTitle) {
        const value = this.currentFormConfig.customTexts.cadastroTitle;
        if (typeof value === 'string') {
          console.log('  ✅ Retornando string:', value);
          return value;
        }
        if (value.enabled && value.content) {
          console.log('  ✅ Retornando content de objeto:', value.content);
          return value.content;
        }
      }
      console.log('  ⚠️ Retornando default: Cadastre-se');
      return "Cadastre-se";
    },
    customCadastroSubtitle() {
      console.log('🔍 customCadastroSubtitle chamado');
      console.log('  isFormStylingActive:', this.isFormStylingActive);
      console.log('  cadastroSubtitle value:', this.currentFormConfig?.customTexts?.cadastroSubtitle);
      if (this.isFormStylingActive && this.currentFormConfig?.customTexts?.cadastroSubtitle) {
        const value = this.currentFormConfig.customTexts.cadastroSubtitle;
        if (typeof value === 'string') {
          console.log('  ✅ Retornando string:', value);
          return value;
        }
        if (value.enabled && value.content) {
          console.log('  ✅ Retornando content de objeto:', value.content);
          return value.content;
        }
      }
      console.log('  ⚠️ Retornando default: Preencha seus dados para criar sua conta.');
      return "Preencha seus dados para criar sua conta.";
    },
    dynamicTextStyles() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return '';
      const config = this.currentFormConfig;
      return `
        .form-container {
          background-color: ${this.hexToRgba(config.backgroundColor, config.opacity / 100)} !important;
          backdrop-filter: blur(16px) !important;
        }
        .form-container h1, .form-container h2, .form-container h3,
        .form-container h4, .form-container h5, .form-container h6,
        .form-container span, .form-container label, .form-container p {
          color: ${config.textColor} !important;
        }
        .form-container input:enabled, .form-container select:enabled, .form-container textarea:enabled {
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .form-container input:disabled, .form-container select:disabled, .form-container textarea:disabled {
          background-color: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container label,
        .login-glass .form-container p,
        .login-glass label,
        .login-glass p {
          color: ${config.textColor} !important;
          background: none !important;
          padding: 0 !important;
        }
        .login-glass .form-container .p-inputtext:enabled,
        .login-glass .form-container input.p-inputtext:enabled,
        .login-glass .form-container input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-dropdown:enabled,
        .login-glass .form-container select:enabled,
        .login-glass .form-container textarea:enabled,
        .login-glass .p-inputtext:enabled,
        .login-glass input.p-inputtext:enabled,
        .login-glass input.p-component.p-inputtext:enabled,
        .login-glass .p-password input.p-inputtext:enabled,
        .login-glass .p-password input.p-component.p-inputtext:enabled,
        .dark .login-glass .p-dropdown,
        .dark .login-glass .p-dropdown.p-component,
        .dark .login-glass .p-select,
        .dark .login-glass .p-select.p-component,
        .dark .login-glass span.p-dropdown.p-component,
        .login-glass .p-dropdown,
        .login-glass .p-dropdown.p-component,
        .login-glass .p-select,
        .login-glass .p-select.p-component {
          background: ${config.inputBgEnabled} !important;
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext:disabled,
        .login-glass .form-container input.p-inputtext:disabled,
        .login-glass .form-container input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-dropdown:disabled,
        .login-glass .form-container select:disabled,
        .login-glass .form-container textarea:disabled,
        .login-glass .p-inputtext:disabled,
        .login-glass input.p-inputtext:disabled,
        .login-glass input.p-component.p-inputtext:disabled,
        .login-glass .p-password input.p-inputtext:disabled,
        .login-glass .p-password input.p-component.p-inputtext:disabled {
          background: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container .p-inputtext,
        .login-glass .form-container input.p-inputtext,
        .login-glass .form-container .p-password input,
        .login-glass .p-inputtext,
        .login-glass input.p-inputtext,
        .login-glass input.p-component.p-inputtext,
        .login-glass .p-password input,
        .login-glass input,
        .login-glass .p-dropdown .p-dropdown-label,
        .login-glass .p-dropdown .p-dropdown-label.p-placeholder,
        .login-glass .p-select .p-select-label,
        .login-glass .p-select .p-select-label.p-placeholder {
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext::placeholder,
        .login-glass .form-container input.p-inputtext::placeholder,
        .login-glass .form-container .p-password input::placeholder,
        .login-glass .p-inputtext::placeholder,
        .login-glass input.p-inputtext::placeholder,
        .login-glass input::placeholder {
          color: ${config.inputTextColor} !important;
          opacity: 0.6 !important;
        }
        .form-container a,
        .form-container ~ div a,
        .form-container ~ .text-center a {
          color: ${config.textColor} !important;
        }
        .form-container button:not([type="submit"]),
        .form-container ~ button:not([type="submit"]),
        button.flex.items-center {
          color: ${config.textColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full,
        .form-container button.btn-dynamic-color,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined),
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) .p-button-label,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) span {
          color: ${config.buttonTextColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full:not(.border),
        .form-container button.btn-dynamic-color,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined),
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined) {
          background-color: ${this.hexToRgba(config.buttonColorType1, config.buttonOpacityType1 / 100)} !important;
          border-color: ${this.hexToRgba(config.buttonColorType1, config.buttonOpacityType1 / 100)} !important;
        }
        .form-container button[type="submit"]:hover,
        .form-container button.w-full:not(.border):hover,
        .form-container button.btn-dynamic-color:hover,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .p-button:not(.p-button-text):not(.p-button-outlined):hover .p-button-label {
          color: ${config.buttonHoverColor} !important;
        }
        button.w-full.border,
        button.h-12.border {
          background-color: ${this.hexToRgba(config.buttonColorType2, config.buttonOpacityType2 / 100)} !important;
          border-color: ${config.buttonColorType2} !important;
        }
      `;
    },
  },
  validations() {
    const validations = {
      email: { required, email },
    };
    if (this.cadastro.selectedTipoCliente === "1") {
      validations.cpf = {
        required,
        validateCpf,
      };
      validations.nomeRazaoSocial = {
        required,
        minLength: minLength(5),
      };
    } else if (this.cadastro.selectedTipoCliente === "2") {
      validations.cnpj = {
        required,
        validateCnpj,
      };
      validations.nomeRazaoSocial = {
        required,
        minLength: minLength(5),
      };
    }
    return { cadastro: validations };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.resetCadastro();
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.resetCadastro();
    next();
  },
  async created() {
    await this.fetchConfiguracao([
      "Remover tipo de cadastro",
      "Modo de autenticação",
      "Redirecionamento do botão entrar Onboarding externo",
      "Exibir apelido",
    ]);

    this.modoAutenticacao =
      this.getConfiguracoes("Modo de autenticação")?.Valor ?? 0;

    this.configuracaoTiposClientes = this.getConfiguracoes(
      "Remover tipo de cadastro",
    );
    this.configuracaoURLEntrar = this.getConfiguracoes(
      "Redirecionamento do botão entrar Onboarding externo",
    );
    this.configuracaoApelido = this.getConfiguracoes("Exibir apelido").Valor;
    if (this.configuracaoURLEntrar) {
      this.loginLink = this.configuracaoURLEntrar.Valor;
    }
  },
  mounted() {
    // Verifica se editor mode está ativo
    if (this.editorModeActive) {
      this.showBackgroundCustomizer = true;
    }
    this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
    if (this.account != null) {
      this.cadastro = this.account;
    }
    if (this.nomeEmpresa == "GENIAL") {
      this.cadastro.selectedTipoCliente = "2";

      // Preencher campos vindos do UserLogin (quando usuário não encontrado)
      if (this.$route.query.email) {
        this.cadastro.email = this.$route.query.email;
      }
      if (this.$route.query.cnpj) {
        this.cadastro.cnpj = this.$route.query.cnpj;
        // Tentar buscar dados da Receita Federal (silenciosamente, sem mostrar erro)
        this.verifyCNPJ(true);
      }
    }
    
    // Preencher tipo de cadastro vindo da query (seleção no UserLogin)
    if (this.$route.query.tipo) {
      this.cadastro.selectedTipoCliente = this.$route.query.tipo;
    }
    
    // Inicia animação de boas-vindas
    this.startTypingAnimation();
    this.$store.commit("SET_MODAL_AVISO_LOGIN", false);
    this.$store.commit("SET_MODAL_AVISO_GENERICO", false);
  },
  methods: {
    ...mapActions({
      signIn: "auth/signIn",
      fetchConfiguracao: "FETCH_CONFIGURACOES",
    }),
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    handleFormStyling(config) {
      console.log('📥 CadastroInicio recebeu formStyling:', config);
      console.log('📝 isFormStylingActive:', config?.enabled?.[this.detectedDevice]);
      console.log('📝 customTexts.desktop:', config?.desktop?.customTexts);
      this.formStylingConfig = config;
    },
    validarCPF(cpf) {
      if (!cpf) return false;
      const cpfLimpo = String(cpf).replace(/[\s.-]*/gim, '');
      
      if (
        cpfLimpo.length !== 11 ||
        cpfLimpo === '00000000000' ||
        cpfLimpo === '11111111111' ||
        cpfLimpo === '22222222222' ||
        cpfLimpo === '33333333333' ||
        cpfLimpo === '44444444444' ||
        cpfLimpo === '55555555555' ||
        cpfLimpo === '66666666666' ||
        cpfLimpo === '77777777777' ||
        cpfLimpo === '88888888888' ||
        cpfLimpo === '99999999999'
      ) {
        return false;
      }
      
      let soma = 0;
      for (let i = 1; i <= 9; i++) {
        soma += parseInt(cpfLimpo.substring(i - 1, i)) * (11 - i);
      }
      let resto = (soma * 10) % 11;
      if (resto === 10 || resto === 11) resto = 0;
      if (resto !== parseInt(cpfLimpo.substring(9, 10))) return false;
      
      soma = 0;
      for (let i = 1; i <= 10; i++) {
        soma += parseInt(cpfLimpo.substring(i - 1, i)) * (12 - i);
      }
      resto = (soma * 10) % 11;
      if (resto === 10 || resto === 11) resto = 0;
      if (resto !== parseInt(cpfLimpo.substring(10, 11))) return false;
      
      return true;
    },
    validarCNPJ(cnpj) {
      if (!cnpj) return false;
      
      const sanitized = String(cnpj).replace(/[^\d]+/g, '');
      const match = sanitized.match(/\d/g);
      const numbers = Array.isArray(match) ? match.map(Number) : [];
      
      if (numbers.length !== 14) return false;
      
      const items = [...new Set(numbers)];
      if (items.length === 1) return false;
      
      const calc = x => {
        const slice = numbers.slice(0, x);
        let factor = x - 7;
        let sum = 0;
        
        for (let i = x; i >= 1; i--) {
          const n = slice[x - i];
          sum += n * factor--;
          if (factor < 2) factor = 9;
        }
        
        const result = 11 - (sum % 11);
        return result > 9 ? 0 : result;
      };
      
      const digits = numbers.slice(12);
      const digit0 = calc(12);
      if (digit0 !== digits[0]) return false;
      
      const digit1 = calc(13);
      return digit1 === digits[1];
    },
    handleBackgroundChange(style) {
      this.customBackgroundStyle = style;
      this.isBackgroundReady = true;
      console.log('✅ Background aplicado e pronto para exibição');
    },
    handleButtonColorChange(color) {
      this.customButtonColor = color;
    },
    handleThemeClick() {
      this.toggleTheme();
      
      // Sincroniza o modo interno do form styling com o tema visual
      if (this.$refs.backgroundCustomizer) {
        const newMode = this.isDark ? 'dark' : 'light';
        console.log(`🔄 Sincronizando form styling para modo ${newMode} (tema visual alterado)`);
        this.$refs.backgroundCustomizer.syncModeWithTheme(newMode);
      }
      
      this.themeClickCount++;
      
      if (this.themeClickTimer) {
        clearTimeout(this.themeClickTimer);
      }
      
      this.themeClickTimer = setTimeout(() => {
        this.themeClickCount = 0;
      }, 2000);
      
      if (this.themeClickCount === 3) {
        this.showSecretInput = true;
        this.secretInput = '';
        this.themeClickCount = 0;
        
        this.$nextTick(() => {
          if (this.$refs.secretInput) {
            this.$refs.secretInput.focus();
          }
        });
        
        setTimeout(() => {
          if (this.secretInput === '') {
            this.showSecretInput = false;
          }
        }, 5000);
      }
    },
    checkSecretCode() {
      const input = this.secretInput.toLowerCase();
      
      if (input === 'editor') {
        this.toggleEditorMode();
        this.showBackgroundCustomizer = this.editorModeActive;
        this.showSecretInput = false;
        this.secretInput = '';
        
        const msg = this.editorModeActive ? 'Editor mode activated' : 'Editor mode deactivated';
        console.log(`🎨 ${msg}`);
      }
      
      if (input.length > 6 && input !== 'editor') {
        this.showSecretInput = false;
        this.secretInput = '';
      }
    },
    startLogoPress() {
      // Apenas no mobile e para empresas DNWAYS
      if (window.innerWidth >= 1024) {
        // Desktop - sempre mostra
        this.showBackgroundCustomizer = true;
        return;
      }
      
      const isDnways = this.nomeEmpresa === 'DNWAYS' || 
                       this.nomeEmpresa === 'DNWAYS_DEMO_Y' || 
                       this.nomeEmpresa === 'DNWAYS_DEMO_X';
      
      if (!isDnways) return;
      
      // Mobile - requer 5 segundos de press
      this.logoPressTimer = setTimeout(() => {
        this.showBackgroundCustomizer = true;
        console.log('🎨 Background customizer ativado!');
      }, 5000);
    },
    endLogoPress() {
      if (this.logoPressTimer) {
        clearTimeout(this.logoPressTimer);
        this.logoPressTimer = null;
      }
    },
    startTypingAnimation() {
      this.showTypingWelcome = true;

      // Define as mensagens com textos customizados
      this.welcomeMessage1 = this.customCadastroTitle;
      this.welcomeMessage2 = this.customCadastroSubtitle;

      // Verifica se a animação está desabilitada via env
      const disableTyping = process.env.VUE_APP_DISABLE_TYPING_ANIMATION === 'true';
      
      if (disableTyping) {
        // Mostra tudo imediatamente sem animação
        this.typedText1 = this.customCadastroTitle;
        this.typedText2 = this.customCadastroSubtitle;
        this.showSecondMessage = true;
        this.showFormContent = true;
        return;
      }

      // Animação normal de digitação
      const typingSpeed = 60; // ms por caractere
      const pauseBetweenMessages = 600; // pausa entre mensagens
      const pauseBeforeForm = 500; // pausa antes de mostrar botões

      // Digita primeira mensagem
      let index1 = 0;
      const typeFirstMessage = () => {
        if (index1 < this.customCadastroTitle.length) {
          this.typedText1 += this.customCadastroTitle[index1];
          index1++;
          setTimeout(typeFirstMessage, typingSpeed);
        } else {
          setTimeout(() => {
            this.showSecondMessage = true;
            typeSecondMessage();
          }, pauseBetweenMessages);
        }
      };
      
      // Digita segunda mensagem
      let index2 = 0;
      const typeSecondMessage = () => {
        if (index2 < this.customCadastroSubtitle.length) {
          this.typedText2 += this.customCadastroSubtitle[index2];
          index2++;
          setTimeout(typeSecondMessage, typingSpeed);
        } else {
          setTimeout(() => {
            this.showFormContent = true;
          }, pauseBeforeForm);
        }
      };
      
      setTimeout(typeFirstMessage, 300);
    },
    voltar() {
      this.openToken = false;
    },
    clear() {
      this.messageError = "";
      this.erro = false;
    },
    apresentarErroVerifyCNPJ(titulo, mensagem) {
      this.messageErrorGenerico =
        titulo ??
        "Não encontramos um registro correspondente ao e-mail ou documento inserido.";
      this.mensagensComplementaresGenerico =
        mensagem ?? "Verifique se digitou corretamente e tente novamente.";

      this.$store.commit("SET_MODAL_AVISO_GENERICO", true);

      this.cadastro = {
        selectedTipoCliente: "2",
        cnpj: null,
        cpf: null,
        nomeRazaoSocial: null,
        email: null,
        apelido: null,
        dataConstituicao: null,
      };
    },
    verifyCNPJ(silent = false) {
      this.requisicaoFinalizada = false;
      const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
      if (this.cadastro.cnpj == "" || this.cadastro.cnpj == null) {
        this.requisicaoFinalizada = true;
        return;
      }
      if (
        this.cadastro.cnpj.length > 0 &&
        !cnpjRegex.test(this.cadastro.cnpj)
      ) {
        this.requisicaoFinalizada = true;
        if (!silent) {
          this.$alert({
            icon: "error",
            title: "Oops...",
            text: `O CNPJ informado é inválido! Verifique os dados e tente novamente.`,
          });
        }
        return;
      }

      api
        .get(`/register/verifyCNPJ?cnpj=${this.cadastro.cnpj}`)
        .then((retorno) => {
          this.requisicaoFinalizada = true;
          if (retorno?.status == 200) {
            if (
              retorno.data.RazaoSocial == "" ||
              retorno.data.RazaoSocial == null
            ) {
              if (!silent) {
                this.apresentarErroVerifyCNPJ();
              }
            } else {
              this.cadastro.nomeRazaoSocial = retorno?.data?.RazaoSocial;
              this.cadastro.dataConstituicao = retorno?.data?.DataConstituicao;
            }
          } else {
            if (!silent) {
              this.apresentarErroVerifyCNPJ();
            }
          }
        })
        .catch((erro) => {
          this.requisicaoFinalizada = true;
          if (!silent) {
            this.apresentarErroVerifyCNPJ(
              "Falha na requisição.",
              "A consulta falhou ao localizar CNPJ, tente novamente.",
            );
          }
          console.error("Erro no verifyCNPJ:", erro);
        });
    },
    limparCadastro() {
      this.cadastro.razaoSocial = null;
      this.cadastro.cnpj = null;
      this.cadastro.cpf = null;
      this.cadastro.nomeRazaoSocial = null;
      this.cadastro.email = null;
      this.cadastro.apelido = null;
      this.cadastro.dataConstituicao = null;
      this.hashToken = "";
    },
    resetCadastro() {
      this.cadastro.selectedTipoCliente = "selecione";
      this.cadastro.razaoSocial = null;
      this.cadastro.cnpj = null;
      this.cadastro.cpf = null;
      this.cadastro.nomeRazaoSocial = null;
      this.cadastro.email = null;
      this.cadastro.apelido = null;
      this.cadastro.dataConstituicao = null;
      this.hashToken = "";
    },
    validarApelido(event) {
      if (this.configuracaoApelido === "S") {
        const value = event.target.value;
        const regex = /[^a-zA-Z0-9]/g;
        this.cadastro.apelido = value.replace(regex, "").slice(0, 20);
      }
    },
    checkApelidoLength(event) {
      if (!this.cadastro.apelido || this.cadastro.apelido.length < 3) {
        this.apelidoError =
          "O campo apelido deve ter no mínimo três caracteres.";
        event.target.focus();
      } else {
        this.apelidoError = "";
      }
    },
    validarAvancar() {
      var nomeCampo = "";
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "" ||
          this.cadastro.selectedTipoCliente == "selecione")
      ) {
        nomeCampo = "Tipo de cadastro";
      }
      if (
        nomeCampo == "" &&
        this.cadastro.selectedTipoCliente == "1" &&
        (this.cadastro.cpf == "" || this.cadastro.cpf == null)
      ) {
        nomeCampo = "CPF";
      }
      // Valida CPF se preenchido
      if (
        nomeCampo == "" &&
        this.cadastro.selectedTipoCliente == "1" &&
        this.cadastro.cpf &&
        !this.validarCPF(this.cadastro.cpf)
      ) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: "O CPF informado é inválido. Verifique os dados e tente novamente.",
        });
        return false;
      }
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "2" ||
          this.cadastro.selectedTipoCliente == "3") &&
        (this.cadastro.cnpj == "" || this.cadastro.cnpj == null)
      ) {
        nomeCampo = "CNPJ";
      }
      // Valida CNPJ se preenchido
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "2" ||
          this.cadastro.selectedTipoCliente == "3") &&
        this.cadastro.cnpj &&
        !this.validarCNPJ(this.cadastro.cnpj)
      ) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: "O CNPJ informado é inválido. Verifique os dados e tente novamente.",
        });
        return false;
      }
      if (
        nomeCampo == "" &&
        this.cadastro.selectedTipoCliente == "1" &&
        (this.cadastro.nomeRazaoSocial == "" ||
          this.cadastro.nomeRazaoSocial == null)
      ) {
        nomeCampo = "Nome completo";
      }
      if (
        nomeCampo == "" &&
        (this.cadastro.selectedTipoCliente == "2" ||
          this.cadastro.selectedTipoCliente == "3") &&
        (this.cadastro.nomeRazaoSocial == "" ||
          this.cadastro.nomeRazaoSocial == null)
      ) {
        nomeCampo = "Razão Social";
      }
      if (
        nomeCampo == "" &&
        (this.cadastro.email == "" || this.cadastro.email == null)
      ) {
        nomeCampo = "E-mail";
      }
      if (
        nomeCampo == "" &&
        this.configuracaoApelido === "S" &&
        (this.cadastro.selectedTipoCliente == "2" ||
          this.cadastro.selectedTipoCliente == "3") &&
        (this.cadastro.apelido == "" || this.cadastro.apelido == null)
      ) {
        nomeCampo = "Apelido";
      }
      if (nomeCampo != "") {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: `É necessário preencher o campo ` + nomeCampo + `.`,
        });
        return false;
      }
      if (
        this.configuracaoApelido === "S" &&
        this.cadastro.selectedTipoCliente != "1"
      ) {
        this.validarApelido({ target: { value: this.cadastro.apelido } });
        if (this.apelidoError) {
          return false;
        }
      }
      return true;
    },
    avancar() {
      if (!this.validarAvancar()) {
        return;
      } else {
        this.$store.commit("SET_CREATE_ACCOUNT", this.cadastro);
        this.$router.push({ name: "CadastroFinalizacao" });
      }
    },
    async sendRegisterWithToken() {
      this.disableBtnConcluir = true;
      if (this.token == "" || this.token == null) {
        this.$alert({
          icon: "error",
          title: "Oops...",
          text: `Token informado é inválido.`,
        });
        this.disableBtnConcluir = false;
        return;
      }
      if (this.validarAvancar()) {
        var registerObj = {
          TipoCadastro: this.cadastro.selectedTipoCliente,
          CpfCnpj: this.cadastro.cnpj,
          NomeRazaoSocial: this.cadastro.nomeRazaoSocial,
          Email: this.cadastro.email,
          DataConstituicao: this.cadastro.dataConstituicao,
          Token: this.token,
          HashToken: this.hashToken,
          Apelido: this.cadastro.apelido,
        };
        try {
          const retorno = await api.post(
            "/register/registerWithToken",
            registerObj,
          );
          this.disableBtnConcluir = false;
          if (
            !typeof retorno.data[0] === "string" &&
            retorno.data[0].includes("Token")
          ) {
            this.$alert({
              icon: "error",
              title: "Oops...",
              text: `Senha informada não atende aos critérios. É necessário possuir o mínimo de 8 caracteres, sendo ao menos 1 letra maiúscula, 1 minúscula e 1 caractere especial.`,
            });
          } else {
            this.hashToken = "";
            this.disableBtnConcluir = false;
            this.$store.commit("SET_CREATE_ACCOUNT", []);

            const model = {
              Usuario: this.cadastro.email,
              Token: this.token,
              Apelido: this.cadastro.apelido,
              TipoCliente: this.cadastro.selectedTipoCliente,
            };

            await this.signIn(model)
              .then((response) => {
                this.limparCadastro();
                this.disableBtnLogin = false;
                if (response.status == 200) {
                  this.messageError = "";
                  this.erro = false;

                  if (this.cadastro.selectedTipoCliente == "1") {
                    this.$router.push({ name: "CadastroPF_Passo_1" });
                  } else if (this.cadastro.selectedTipoCliente == "2") {
                    this.$router.push({ name: "CadastroPJ_Passo_1" });
                  } else if (this.cadastro.selectedTipoCliente == "3") {
                    this.$router.push({ name: "CadastroFI_Passo_1" });
                  }
                }
              })
              .catch((error) => {
                this.limparCadastro();
                this.disableBtnLogin = false;
                if (error.response.data == "") {
                  this.messageError =
                    "Falha na autenticação. Entre em contato com o suporte.";
                  this.erro = true;
                } else if (
                  error.response.data?.data[0] ==
                  "Cliente não validado o e-mail!"
                ) {
                  this.messageError =
                    "Falha na autenticação. Confirme seu endereço de e-mail.";
                  this.erro = true;
                  this.habilitarReenviarEmail = true;
                } else {
                  this.messageError = error.response.data?.data[0];
                  this.erro = true;
                }
              });
          }
        } catch (error) {
          console.error("Erro ao criar a conta:", error);

          const descricao = error?.response?.data?.descricao || "";
          const iconType = descricao.includes("Esse e-mail já foi cadastrado")
            ? "warning"
            : "error";

          this.$alert({
            icon: iconType,
            title: iconType == "warning" ? "Atenção!" : "Erro",
            text:
              error?.response?.data?.descricao != ""
                ? error?.response?.data?.descricao
                : "Dados inválidos",
          });
        } finally {
          this.disableBtnConcluir = false;
        }
      } else {
        this.disableBtnReceberToken = false;
      }
    },
    async receberToken() {
      this.disableBtnReceberToken = true;
      if (this.validarAvancar()) {
        await api
          .post("/register/sendEmailPreRegister", this.cadastro)
          .then((retorno) => {
            this.hashToken = retorno.data;
            this.openToken = true;
            this.disableBtnReceberToken = false;
          })
          .catch((error) => {
            this.$alert({
              icon: "error",
              title: "Erro",
              text:
                error?.response?.data?.data != ""
                  ? error?.response?.data?.data
                  : "Dados inválidos",
            }).then(() => {
              this.$router.push({ name: "UserLogin" });
            });
          });
        return;
      } else {
        this.disableBtnReceberToken = false;
      }
    },
  },
  watch: {
    customCadastroTitle(newVal) {
      console.log('🔄 customCadastroTitle mudou para:', newVal);
      this.welcomeMessage1 = newVal;
      // Se a animação está desabilitada, atualiza o texto diretamente
      if (process.env.VUE_APP_DISABLE_TYPING_ANIMATION === 'true') {
        this.typedText1 = newVal;
      }
    },
    customCadastroSubtitle(newVal) {
      console.log('🔄 customCadastroSubtitle mudou para:', newVal);
      this.welcomeMessage2 = newVal;
      // Se a animação está desabilitada, atualiza o texto diretamente
      if (process.env.VUE_APP_DISABLE_TYPING_ANIMATION === 'true') {
        this.typedText2 = newVal;
      }
    },
    consultarPermissaoAvancar() {
      this.permitirAvancar = false;
      if (this.cadastro.selectedTipoCliente == "1") {
        if (
          this.cadastro.nomeCompleto &&
          this.cadastro.nomeCompleto.length > 5 &&
          this.validarCPF(this.cadastro.CPF)
        ) {
          this.permitirAvancar = true;
        }
      } else if (
        this.cadastro.selectedTipoCliente == "2" ||
        this.cadastro.selectedTipoCliente == "3"
      ) {
        if (
          this.cadastro.razaoSocial &&
          this.cadastro.CNPJ &&
          this.validarCNPJ(this.cadastro.CNPJ)
        ) {
          this.permitirAvancar = true;
        }
      }
    },
    configuracaoTiposClientes(newVal) {
      if (newVal && newVal.Valor) {
        const valores = newVal.Valor.replace("\r\n", ";")
          .replace(",", ";")
          .split(";")
          .map((item) => item.trim())
          .filter((item) => item);
        const todasOpcoes = [
          { value: "1", label: "Pessoa Física" },
          { value: "2", label: "Pessoa Jurídica" },
          { value: "3", label: "Fundo de Investimento" },
        ];
        const tiposFiltrados = todasOpcoes.filter(
          (opcao) => !valores.includes(opcao.value),
        );
        if (
          JSON.stringify(tiposFiltrados) !== JSON.stringify(this.tiposClientes)
        ) {
          this.tiposClientes = tiposFiltrados;
        }
      } else {
        if (this.tiposClientes.length > 0) {
          this.tiposClientes = [];
        }
      }
    },
  },
};
</script>
